"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, Row, StickyBar, Stepper, TopBar } from "@/components/ui";
import { FoodImg } from "@/components/FoodImg";
import { samePromotion, useCart } from "@/store/cart";
import type { Promotion } from "@/api/online-ordering-service/model";
import { useGroupCart } from "@/store/group-cart";
import { GroupCartView } from "@/components/group/GroupCartView";
import { RecommendationStrip } from "@/components/RecommendationStrip";
import { useBranch } from "@/store/branch";
import { useTable } from "@/store/table";
import { useFulfillment } from "@/store/fulfillment";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
import { useBranchEta, useMinOrder, useSyncNearestBranch } from "@/hooks/useBranchInfo";
import { useBranchOpen } from "@/hooks/useBranchOpen";
import { useAddresses } from "@/hooks/useAddresses";
import { localize, useMenu } from "@/hooks/useMenu";
import { describeCategoryBundle } from "@/lib/promotion-describe";
import { usePromotions, usePromotionIdentity } from "@/hooks/usePromotions";
import { track } from "@/lib/analytics";
import { useTillPricing } from "@/hooks/useTillPricing";
import { GrantedLineRow } from "@/components/checkout/GrantedLineRow";
import { asCategoryBundle, promotionsApi } from "@/api/services";
import {
  addressDisplayLine,
  branchAddressLine,
  branchDisplayName,
} from "@/lib/fulfillment";

function injectPrice(text: string, amount: number) {
  const parts = text.split("___PRICE___");
  if (parts.length === 1) return text;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
      {parts[0]}
      <Price amount={amount} />
      {parts[1]}
    </span>
  );
}

export function CartScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    cart,
    updateQty,
    removeLine,
    orderNote,
    setOrderNote,
    appliedPromotions,
    setAppliedPromotion,
    removeAppliedPromotion,
  } = useCart();
  const groupCart = useGroupCart();
  const { branchId, branch, requiresBranchSelection } = useBranch();
  const { isDineIn } = useTable();
  const { mode, selectedAddressId } = useFulfillment();
  const { addresses } = useAddresses();
  const { format } = useFormatPrice();
  const { etaMin, etaMax } = useBranchEta();
  const { isClosed, opensAt } = useBranchOpen();
  const minOrder = useMinOrder();
  useSyncNearestBranch();
  const { items: menuItems, categories: menuCategories, menuTaxExempt } = useMenu();
  const { promotions } = usePromotions();
  const tillPricing = useTillPricing({ menuItems, menuTaxExempt, promotions });
  const promoIdentity = usePromotionIdentity();
  const [promo, setPromo] = useState("");
  const [promoError, setPromoError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  // Priced exactly as the POS till prices the same order (owner ruling
  // 2026-10-10): every eligible auto-apply promotion plus the entered code's
  // one, stacked by priority, VAT on the discounted net, free items added.
  const { pricing } = tillPricing;
  const appliedPromotionIds = pricing.appliedPromotionIds;
  const cartGross = cart.reduce((s, l) => s + l.price * l.qty, 0);
  const subtotal = pricing.itemsSubtotal;
  const discount = Math.round((pricing.promotionAmount + pricing.discountAmount) * 100) / 100;
  const deliveryFee = pricing.deliveryFeeAmount;
  const serviceTax = pricing.serviceFeeAmount;
  const tax = pricing.taxAmount;
  const total = pricing.totalAmount;
  // An entered code is accepted for this branch/order type yet may not apply
  // (the cart doesn't meet its criteria yet, or it can't combine with another
  // applied offer): it is "effective" — green badge — only when the till
  // applied it; otherwise a hint shows under its chip.
  const isEffective = (p: Promotion) => !!p.id && appliedPromotionIds.includes(p.id);
  const notAppliedHint = (p: Promotion) =>
    appliedPromotions.length > 1 && (p.stackable !== true || appliedPromotions.some((o) => o !== p && o.stackable !== true))
      ? t("v2.cart.promo_not_combinable")
      : t("v2.cart.promo_not_qualifying");
  const selectedAddress =
    addresses.find((a) => a.id === selectedAddressId) ?? null;
  const destinationLine =
    mode === "delivery"
      ? selectedAddress
        ? addressDisplayLine(selectedAddress)
        : t("v2.home.choose_delivery_address")
      : branchDisplayName(branch, i18n.language || "en") ||
        branchId ||
        t("v2.home.choose_pickup_branch");
  const branchAddress = branchAddressLine(branch, i18n.language || "en");
  const isBelowMinOrder = mode === "delivery" && minOrder > 0 && cartGross < minOrder;
  const minOrderDiff = isBelowMinOrder ? minOrder - cartGross : 0;
  const checkoutBlocked = requiresBranchSelection || isClosed || isBelowMinOrder;

  async function onApply() {
    const code = promo.trim();
    if (!code || !branchId || validating) return;
    setValidating(true);
    setPromoError(null);
    try {
      const match = await promotionsApi.validateCode(branchId, code, {
        orderType: mode,
        ...promoIdentity,
      });
      if (!match) {
        setPromoError(t("v2.cart.promo_invalid"));
        track("promo_code_rejected", { code });
        return;
      }
      if (appliedPromotions.some((p) => samePromotion(p, match))) {
        setPromoError(t("v2.cart.promo_already_applied"));
        return;
      }
      setAppliedPromotion(match);
      setPromo("");
      track("promo_code_applied", {
        code,
        promotion_id: match.id ?? null,
        discount_type: match.reward?.discountType ?? null,
        discount_value: match.reward?.discountValue ?? null,
      });
    } catch (err) {
      setPromoError(
        err instanceof Error ? err.message : t("v2.cart.promo_validation_failed"),
      );
    } finally {
      setValidating(false);
    }
  }

  function onRemovePromo(p: Promotion) {
    removeAppliedPromotion(p);
    setPromoError(null);
  }

  function promoLabel(p: Promotion): string {
    const bundle = asCategoryBundle(p.reward);
    if (bundle) {
      return ` — ${describeCategoryBundle(bundle, {
        categoryName: (id) => menuCategories.find((c) => c.id === id)?.label,
        itemName: (id) => menuItems.find((m) => m.id === id)?.name,
        formatPrice: format,
        any: (name) => t("v2.cart.promo_bundle_any", { name }),
        forPrice: (groups, price) => t("v2.cart.promo_bundle_for", { groups, price }),
      })}`;
    }
    return p.reward?.discountType === "percentage" && p.reward?.discountValue != null
      ? ` — ${t("v2.cart.promo_percent_off", { value: p.reward.discountValue })}`
      : "";
  }

  // Group-active short-circuit: replace the whole cart UI with the live
  // GroupCartView (separate render path keeps the local-cart promo/delivery
  // logic out of this dine-in flow). The local cart still exists in storage
  // as the seed for createGroup; we don't touch it here.
  if (groupCart.status === "active") {
    return <GroupCartView />;
  }

  if (cart.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <TopBar title={t("v2.cart.title")} onBack={() => navigate(-1)} />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 32px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              color: "var(--primary-700)",
              marginBottom: 18,
            }}
          >
            <Icons.bag size={40} />
          </div>
          <div className="display" style={{ fontSize: 20 }}>
            {t("v2.cart.empty_title")}
          </div>
          <div style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.5 }}>
            {t("v2.cart.empty_desc")}
          </div>
          <Button size="lg" onClick={() => navigate("/")} style={{ marginTop: 20 }}>
            {t("v2.cart.browse_menu")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 200 }}>
      <TopBar title={t("v2.cart.title")} onBack={() => navigate(-1)} />

      {!isDineIn && (
        <div style={{ padding: "12px 16px 8px" }}>
          <Card
            padding={14}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "var(--primary-50)",
              border: "1px solid var(--primary-100)",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                background: "var(--surface-1)",
                display: "grid",
                placeItems: "center",
                color: "var(--primary-700)",
              }}
            >
              {mode === "delivery" ? <Icons.bike size={18} /> : <Icons.bag size={18} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--primary-800)" }}>
                {etaMin != null && etaMax != null
                  ? mode === "delivery"
                    ? t("v2.cart.delivery_in_min", { min: etaMin, max: etaMax })
                    : t("v2.cart.pickup_in_min", { min: etaMin, max: etaMax })
                  : mode === "delivery"
                  ? t("v2.cart.delivery_label", "Delivery")
                  : t("v2.cart.pickup_label", "Pickup")}
              </div>
              <div style={{ fontSize: 12, color: "var(--primary-700)", marginTop: 2 }}>
                {mode === "delivery" ? destinationLine : branchAddress || destinationLine}
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              style={{
                background: "transparent",
                border: "1px solid var(--primary-700)",
                color: "var(--primary-800)",
                borderRadius: 999,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t("v2.common.change")}
            </button>
          </Card>
        </div>
      )}

      <div
        style={{
          padding: "8px 16px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "var(--ink-2)",
            letterSpacing: 0.2,
            textTransform: "uppercase",
            marginTop: 6,
          }}
        >
          {t(cart.length === 1 ? "v2.cart.items_one" : "v2.cart.items_other", { count: cart.length })}
        </div>
        {cart.map((line) => (
          <Card
            key={line.id}
            padding={12}
            style={{ display: "grid", gridTemplateColumns: "64px 1fr auto", gap: 12 }}
          >
            <FoodImg
              tone={line.tone}
              square
              rounded={10}
              src={line.imageUrl}
              alt={line.name}
            />
            <div style={{ minWidth: 0 }}>
              <div className="display" style={{ fontSize: 14, color: "var(--ink-1)", lineHeight: 1.2 }}>
                {line.name}
              </div>
              {line.modsLabel?.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4, lineHeight: 1.4 }}>
                  {line.modsLabel.join(" · ")}
                </div>
              )}
              {line.notes && (
                <div
                  style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4, fontStyle: "italic" }}
                >
                  "{line.notes}"
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                {line.isReward ? (
                  <>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}>
                      Qty: {line.qty}
                    </span>
                    <Badge tone="primary">Reward</Badge>
                  </>
                ) : (
                  <>
                    <Stepper value={line.qty} onChange={(v) => updateQty(line.id, v)} size="sm" />
                    <button
                      type="button"
                      onClick={() => navigate(`/item/${line.itemId}?editCartLineId=${line.id}`)}
                      style={{
                        background: "rgba(0,0,0,0.04)",
                        border: 0,
                        color: "var(--ink-3)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "inherit",
                        padding: "6px 10px",
                        borderRadius: 6,
                        whiteSpace: "nowrap",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      <Icons.edit size={14} /> {t("v2.cart.edit_line", "Edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(line.id)}
                      style={{
                        background: "rgba(0,0,0,0.04)",
                        border: 0,
                        color: "var(--ink-3)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "inherit",
                        padding: "6px 10px",
                        borderRadius: 6,
                        whiteSpace: "nowrap",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      <Icons.trash size={14} /> {t("v2.cart.remove_line", "Remove")}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="num" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
              <Price amount={line.price * line.qty} />
            </div>
          </Card>
        ))}

        <button
          onClick={() => navigate("/")}
          style={{
            padding: "12px 14px",
            borderRadius: "var(--radius-card)",
            border: "1px dashed var(--border-strong)",
            background: "transparent",
            color: "var(--ink-2)",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Icons.plus size={14} /> {t("v2.cart.add_more_items")}
        </button>
      </div>

      {/* Free items a promotion granted (the till adds them on its own). */}
      {pricing.freeItemLines.length > 0 && (
        <div style={{ padding: "0 16px 16px", display: "grid", gap: 8 }}>
          {pricing.freeItemLines.map((g, idx) => (
            <GrantedLineRow
              key={`${g.promotionId}-${g.itemId}-${idx}`}
              name={menuItems.find((m) => m.id === g.itemId)?.name || g.itemId}
              promoName={
                localize(promotions.find((p) => p.id === g.promotionId)?.name ?? null, i18n.language || "en") ||
                t("v2.checkout.free_item_promo", { defaultValue: "promotion" })
              }
              subtotal={g.unitPrice * g.quantity}
            />
          ))}
        </div>
      )}

      {/* Recommendation strip (FR-019 Phase 1) — "You might also like",
          refetches as cart contents change; hides itself when the engine
          returns nothing or fails. */}
      <RecommendationStrip trigger="cart_view" style={{ padding: "0 16px 16px" }} />

      <div style={{ padding: "0 16px 16px" }}>
        <Card padding={12}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-2)", textTransform: "uppercase" }}>
            {t("v2.cart.order_note", "Order note")}
          </div>
          <textarea
            value={orderNote}
            onChange={(e) => setOrderNote(e.target.value.slice(0, 250))}
            placeholder={t("v2.cart.order_note_placeholder", "Add notes for your order")}
            rows={3}
            style={{
              width: "100%",
              marginTop: 8,
              border: "1px solid var(--border)",
              borderRadius: 10,
              background: "var(--surface-1)",
              color: "var(--ink-1)",
              fontSize: 14,
              fontFamily: "inherit",
              padding: "10px 12px",
              resize: "vertical",
              outline: "none",
              minHeight: 72,
            }}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--ink-3)", textAlign: "end" }}>
            {orderNote.length}/250
          </div>
        </Card>
      </div>

      {/* Promo-code entry is always available: a customer may hold a code that
          isn't in the branch's auto-listed promotions. Don't gate on
          `promotions.length`. */}
      <div style={{ padding: "0 16px 16px" }}>
        <Card padding={0}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "var(--surface-2)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ink-2)",
                }}
              >
                <Icons.ticket size={18} />
              </div>
              <input
                value={promo}
                onChange={(e) => {
                  setPromo(e.target.value);
                  if (promoError) setPromoError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onApply();
                }}
                placeholder={t("v2.cart.promo_placeholder")}
                style={{
                  flex: 1,
                  border: 0,
                  background: "transparent",
                  outline: "none",
                  fontSize: 14,
                  fontFamily: "inherit",
                  color: "var(--ink-1)",
                }}
              />
              <button
                onClick={onApply}
                disabled={promo.trim().length === 0 || validating}
                style={{
                  padding: "8px 14px",
                  borderRadius: "var(--radius-chip)",
                  border: 0,
                  background:
                    promo.trim() ? "var(--ink-1)" : "var(--surface-2)",
                  color: promo.trim() ? "#fff" : "var(--ink-3)",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor:
                    promo.trim() ? "pointer" : "default",
                  fontFamily: "inherit",
                }}
              >
                {validating ? "…" : t("v2.common.apply")}
              </button>
            </div>
            {promoError && (
              <div
                style={{
                  padding: "0 12px 12px",
                  fontSize: 12,
                  color: "#b91c1c",
                }}
              >
                {promoError}
              </div>
            )}
            {appliedPromotions.map((p) => (
              <div key={p.id ?? p.promoCode ?? ""} style={{ padding: "0 12px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge tone={isEffective(p) ? "success" : "neutral"}>
                    {isEffective(p) && <><Icons.check size={11} />{" "}</>}
                    {p.promoCode ?? t("v2.cart.promo")}
                    {promoLabel(p)}
                  </Badge>
                  <button
                    onClick={() => onRemovePromo(p)}
                    aria-label={t("v2.common.remove")}
                    style={{
                      marginInlineStart: "auto", border: 0, background: "transparent", padding: 4,
                      color: "var(--ink-3)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {t("v2.common.remove")}
                  </button>
                </div>
                {!isEffective(p) && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#9a3412", lineHeight: 1.45 }}>
                    {notAppliedHint(p)}
                  </div>
                )}
              </div>
            ))}
            {appliedPromotions.some(isEffective) && (
              <div style={{ padding: "0 12px 12px", fontSize: 12, color: "var(--ink-3)" }}>
                {t("v2.cart.saved_amount", { amount: format(discount) })}
              </div>
            )}
          </Card>
        </div>

      <div style={{ padding: "0 16px" }}>
        <Card padding={14}>
          <Row label={t("v2.cart.subtotal")} value={<Price amount={subtotal} />} />
          {discount > 0 && <Row label={t("v2.cart.promo_discount")} value={<span style={{color: "var(--color-primary-main)"}}>−<Price amount={discount} /></span>} accent />}
          {mode === "delivery" && deliveryFee > 0 && <Row label={t("v2.cart.delivery")} value={<Price amount={deliveryFee} />} />}
          <Row label={t("v2.cart.estimated_tax")} value={<Price amount={tax} />} />
          {serviceTax > 0 && (
            <Row label={t("v2.cart.service_tax")} value={<Price amount={serviceTax} />} />
          )}
          <div style={{ height: 1, background: "var(--border)", margin: "10px 0" }} />
          <Row label={t("v2.cart.total")} value={<Price amount={total} />} bold />
        </Card>
      </div>

      {checkoutBlocked && (
        <div style={{ padding: "12px 16px 0" }}>
          <Card padding={12} style={{ background: "#fff7ed", borderColor: "#fed7aa" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Icons.info size={16} style={{ color: "#9a3412", marginTop: 1 }} />
              <div style={{ fontSize: 13, color: "#9a3412", fontWeight: 600, lineHeight: 1.45 }}>
                {requiresBranchSelection
                  ? t("v2.cart.select_branch_before_checkout")
                  : isClosed
                  ? opensAt
                    ? t("v2.cart.closed_before_checkout_opens", { time: opensAt })
                    : t("v2.cart.closed_before_checkout")
                  : isBelowMinOrder
                  ? injectPrice(
                      t("v2.cart.min_order_gap_notice", {
                        min: "___PRICE___",
                        defaultValue: "Minimum order for delivery is ___PRICE___",
                      }),
                      minOrder
                    )
                  : ""}
              </div>
            </div>
          </Card>
        </div>
      )}

      <StickyBar
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
          paddingBottom: "12px",
        }}
      >
        <Button
          full
          size="lg"
          testId="cart-checkout"
          onClick={() => {
            if (checkoutBlocked) {
              if (requiresBranchSelection) navigate("/specify-location?returnTo=home");
              return;
            }
            track("checkout_started", {
              items_count: cart.length,
              units_count: cart.reduce((n, l) => n + l.qty, 0),
              subtotal,
              total,
              promo_applied: appliedPromotions.length > 0,
              promo_codes_count: appliedPromotions.length,
            });
            navigate("/checkout", { state: { total } });
          }}
          disabled={checkoutBlocked}
          trailing={!checkoutBlocked ? <Icons.chevron size={18} /> : undefined}
        >
          {requiresBranchSelection
            ? t("v2.cart.select_branch_before_checkout")
            : isClosed
            ? t("v2.cart.closed_before_checkout")
            : isBelowMinOrder
            ? injectPrice(
                t("v2.cart.min_order_btn", {
                  amount: "___PRICE___",
                  defaultValue: "Min order ___PRICE___",
                }),
                minOrder
              )
            : injectPrice(t("v2.cart.checkout_total", { total: "___PRICE___" }), total)}
        </Button>
      </StickyBar>
    </div>
  );
}
